import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, GBP } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, UrgentTag } from "@/components/StatusBadge";
import { Users, Hammer, PackageCheck, Clock, CreditCard, Images, Wrench, ArrowUpRight, Vault } from "lucide-react";

function Metric({ label, value, accent, testid, onClick }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="panel rounded-sm p-5 text-left transition-all hover:border-[#3E382A]">
      <div className="eyebrow mb-2">{label}</div>
      <div className="text-3xl font-semibold tracking-tight num" style={{ color: accent || "#F5F3EF" }}>{value}</div>
    </button>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/admin").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-[#8C867A]">Loading…</div>;

  return (
    <div>
      <PageHeader eyebrow="3B OS · Operations" title="Studio Overview" />

      {/* Revenue band */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4 rise">
        <Metric label="Total Revenue" value={GBP(d.total_revenue)} accent="#D4AF37" testid="metric-total-revenue" onClick={() => navigate("/financials")} />
        <Metric label="Membership Rev." value={GBP(d.monthly_membership_revenue)} testid="metric-membership-revenue" onClick={() => navigate("/financials")} />
        <Metric label="Trade Revenue" value={GBP(d.trade_revenue)} testid="metric-trade-revenue" onClick={() => navigate("/financials")} />
        <Metric label="Education Rev." value={GBP(d.education_revenue)} testid="metric-education-revenue" onClick={() => navigate("/financials")} />
        <Metric label="Active Members" value={`${d.active_members} / ${d.total_members}`} testid="metric-active-members" onClick={() => navigate("/members")} />
      </div>

      {/* Deposit liability — separated */}
      <div className="panel rounded-sm p-4 mb-4 flex items-center gap-4 rise" style={{ borderColor: "#3E382A" }}>
        <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: "rgba(212,175,55,0.12)" }}>
          <Vault size={19} className="brass-text" />
        </div>
        <div className="flex-1">
          <div className="eyebrow">Deposit Funds Held in Trust — Not Revenue</div>
          <div className="text-2xl font-semibold brass-text num">{GBP(d.deposit_liabilities)}</div>
        </div>
        <div className="text-xs text-[#8C867A] max-w-[220px] hidden sm:block">Security deposit contributions are recorded as a liability, kept separate from operating revenue.</div>
      </div>

      {/* Operational metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6 rise">
        <Metric label="Active Jobs" value={d.active_trade_jobs} testid="metric-active-jobs" onClick={() => navigate("/board")} />
        <Metric label="Jobs Ready" value={d.jobs_ready} accent="#10B981" testid="metric-jobs-ready" onClick={() => navigate("/board")} />
        <Metric label="Overdue Jobs" value={d.overdue_jobs} accent={d.overdue_jobs ? "#F59E0B" : "#F5F3EF"} testid="metric-overdue" onClick={() => navigate("/jobs")} />
        <Metric label="Outstanding £" value={GBP(d.outstanding_payments)} accent={d.outstanding_payments ? "#F59E0B" : "#F5F3EF"} testid="metric-outstanding" onClick={() => navigate("/payments")} />
        <Metric label="Content Queue" value={d.content_pending} testid="metric-content" onClick={() => navigate("/content")} />
        <Metric label="Equip. Issues" value={d.equipment_issues} accent={d.equipment_issues ? "#F59E0B" : "#F5F3EF"} testid="metric-issues" onClick={() => navigate("/workshop")} />
      </div>

      <div className="panel rounded-sm p-5 rise">
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">Latest Trade Jobs</div>
          <button onClick={() => navigate("/board")} className="text-xs brass-text link-underline flex items-center gap-1">Trade board <ArrowUpRight size={12} /></button>
        </div>
        <div className="divide-y" style={{ borderColor: "#28282D" }}>
          {d.recent_jobs.map((j) => (
            <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`admin-job-${j.id}`}
              className="w-full flex items-center justify-between gap-3 py-3.5 text-left hover:bg-[#1A1A1E] px-2 -mx-2 rounded-sm transition-colors">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs brass-text">{j.job_number}</span>
                  {j.priority === "URGENT" && <UrgentTag />}
                </div>
                <div className="text-sm text-[#E2DFD7] truncate mt-0.5">{j.title}</div>
              </div>
              <div className="text-xs text-[#8C867A] hidden sm:block w-32 truncate">{j.member_name}</div>
              <StatusBadge status={j.status} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
